import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { router } from '@inertiajs/react';
import { cn } from '../../lib/cn';
import { Button, Textarea, InputLabel } from '../ui';

export default function ImportModal({ lessonId, activityId, onClose, onImportStarted }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState(null); // 'file' | 'paste'
  const [rawText, setRawText] = useState('');
  const [file, setFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef(null);

  const canSubmit = mode === 'paste' ? rawText.trim() : file;

  // Infer input_type from file extension
  const inferType = (f) => {
    const ext = f.name.split('.').pop().toLowerCase();
    if (ext === 'pdf') return 'pdf';
    return 'docx';
  };

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    if (selected) {
      setFile(selected);
      setMode('file');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const dropped = e.dataTransfer.files?.[0];
    if (dropped) {
      setFile(dropped);
      setMode('file');
    }
  };

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);

    const formData = new FormData();
    if (mode === 'paste') {
      formData.append('question_import[input_type]', 'pasted_text');
      formData.append('question_import[raw_text]', rawText);
    } else {
      formData.append('question_import[input_type]', inferType(file));
      formData.append('question_import[uploaded_file]', file);
    }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;

    try {
      const importUrl = activityId
        ? `/teacher/activities/${activityId}/question_imports`
        : `/teacher/lessons/${lessonId}/question_imports`;
      const res = await fetch(importUrl, {
        method: 'POST',
        headers: { 'X-CSRF-Token': csrfToken },
        body: formData,
      });

      if (activityId && res.ok && onImportStarted) {
        const data = await res.json();
        onImportStarted(data);
        onClose?.();
      } else if (res.redirected) {
        window.location.href = res.url;
      } else if (res.ok) {
        onClose?.();
        router.reload();
      } else {
        const text = await res.text();
        setError(text || 'Import failed');
      }
    } catch {
      setError('Network error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />

      {/* Modal */}
      <div className="relative z-10 bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-lg mx-4">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{t('questions.importQuestions')}</h2>
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* File Dropzone */}
          <div
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            onClick={() => { fileRef.current?.click(); }}
            className={cn(
              'border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-all duration-200',
              file
                ? 'border-gray-900 bg-gray-50 dark:border-white dark:bg-gray-800/50'
                : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800/50'
            )}
          >
            <input
              ref={fileRef}
              type="file"
              accept=".docx,.pdf"
              onChange={handleFileChange}
              className="hidden"
            />
            {file ? (
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white truncate max-w-full text-center">{file.name}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {(file.size / 1024).toFixed(1)} KB — Click to change
                </p>
              </div>
            ) : (
              <div>
                <svg className="w-8 h-8 text-gray-400 dark:text-gray-500 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                </svg>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('questions.dropFile')}
                </p>
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="flex items-center gap-3 my-4">
            <div className="flex-1 border-t border-gray-200 dark:border-gray-700" />
            <span className="text-xs text-gray-400 dark:text-gray-500 uppercase">{t('common.or')}</span>
            <div className="flex-1 border-t border-gray-200 dark:border-gray-700" />
          </div>

          {/* Paste Text */}
          <div>
            <Textarea
              id="paste-text"
              value={rawText}
              onChange={(e) => { setRawText(e.target.value); if (e.target.value.trim()) setMode('paste'); }}
              onFocus={() => { if (!file) setMode('paste'); }}
              placeholder={t('questions.pasteQuestions')}
              rows={5}
              className={cn(
                file && rawText.trim() === '' && 'opacity-50'
              )}
            />
            {rawText.trim() && (
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                {rawText.trim().split('\n').length} {t('questions.lines')}
              </p>
            )}
          </div>

          {/* Error */}
          {error && (
            <p className="text-sm text-red-500 mt-4">{error}</p>
          )}

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 mt-6">
            <Button variant="ghost" size="sm" onClick={onClose}>{t('common.cancel')}</Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSubmit}
              disabled={!canSubmit || submitting}
            >
              {submitting ? t('common.processing') : t('questions.process')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
